import Axios from 'axios';
import { toastr } from 'react-redux-toastr';
import { get } from 'lodash';
import moment from 'moment';
import queryString from '../../../helpers/queryString';
import handleErrorResponse from '../../../helpers/handleErrorResponse';
import * as policyActions from './policyReducer';
import { ADD_RESOURCE } from '../../layout/redux/breadcrumb';

const defaultIncludes = [
  'customer',
  'user',
  'broker',
  'broker.organisation',
  'non_scheme_documents',
  'premiums',
  'premiums.scheme',
  'premiums.scheme.product',
  'schemes.product',
  'documents',
  'product',
  'mid_term_adjustment_applications',
  'source',
  'channel',
  'campaign',
  'renewal_policy_application',
];

const fromApi = (application) => {
  if (get(application, 'data.attributes.metadata.adults')) {
    application.data.attributes.metadata.adults = application.data.attributes.metadata.adults.map(
      (adult) => {
        adult.dob = dateFromApi(adult.dob);
        return adult;
      },
    );
  }

  if (get(application, 'data.attributes.metadata.children')) {
    application.data.attributes.metadata.children = application.data.attributes.metadata.children.map(
      (child) => {
        child.dob = dateFromApi(child.dob);
        return child;
      },
    );
  }

  if (get(application, 'data.attributes.metadata.travellers')) {
    application.data.attributes.metadata.travellers = application.data.attributes.metadata.travellers.map(
      (traveller) => {
        traveller.dob = dateFromApi(traveller.dob);
        return traveller;
      },
    );
  }

  return application;
};

export function createAccount(id) {
  const endpoint = `customers/${id}:create-account`;

  return () => {
    return Axios.post(endpoint)
      .then(() => {
        toastr.success('Success');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to create account');
      });
  };
}

const dateFromApi = (date) => {
  if (!date) {
    return '';
  }

  const formattedDate = moment(date)
    .utcOffset(0, true)
    .format('DD/MM/YYYY');

  return formattedDate !== 'Invalid Date' ? formattedDate : '';
};

export function clearData() {
  return {
    type: policyActions.CLEAR_DATA,
  };
}

export function isSubmitting(status) {
  return {
    type: policyActions.IS_SUBMITTING,
    status,
  };
}

export function isCheckingCancel(status) {
  return {
    type: policyActions.IS_CHECKING_CANCEL,
    status,
  };
}

export function isGeneratingDocs(status) {
  return {
    type: policyActions.IS_GENERATING_DOCS,
    status,
  };
}

export function getPolicy(id, isApplication = false, includes = defaultIncludes) {
  return (dispatch) => {
    if (isApplication && includes.indexOf('policy') === -1) {
      includes.push('policy');
      includes.push('transactions');
    } else {
      includes.push('issued_applications');
      includes.push('issued_applications.transactions');
    }

    let endpoint = `policies/${isApplication ? 'applications/' : ''}${id}`;
    endpoint += queryString(includes);

    return Axios.get(endpoint).then((response) => {
      dispatch({
        type: policyActions.SET_POLICY,
        data: fromApi(response.data),
      });
      dispatch({
        type: ADD_RESOURCE,
        id: response.data.data.id,
        name: isApplication
          ? response.data.data.attributes.quote_reference
          : response.data.data.attributes.policy_number,
      });
      return response.data;
    });
  };
}

export function generateDocuments(application, resourceType, callback) {
  return (dispatch) => {
    dispatch(isGeneratingDocs(true));
    return Axios.post(`${resourceType}/${application.data.id}:generate-documents`, {})
      .then(() => {
        toastr.success('Success', 'Documents have been generated');
        dispatch(isGeneratingDocs(false));

        if (callback) {
          callback();
        }

        return true;
      })
      .catch((error) => {
        dispatch(isGeneratingDocs(false));
        handleErrorResponse(error, 'Documents failed to generate');
      });
  };
}

export function downloadDocuments(values, applicationId, resourceType) {
  return () => {
    return Axios.post(`${resourceType}/${applicationId}:download-documents`, values)
      .then((response) => {
        window.open(response.data.meta.url, '_blank');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to download documents');
      });
  };
}
export function printDocuments(values, applicationId, resourceType) {
  return () => {
    return Axios.post(`${resourceType}/${applicationId}:print-documents`, values)
      .then((response) => {
        window.open(response.data.meta.url, '_blank');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to download documents');
      });
  };
}

export function mergeAndArchiveDocuments(values, applicationId, resourceType) {
  return () => {
    return Axios.post(
      `${resourceType}/${applicationId}:merge-and-archive-documents`,
      values,
    )
      .then((response) => {
        window.open(response.data.meta.url, '_blank');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to download documents');
      });
  };
}

export function getQuotesHistory(quoteReference) {
  return (dispatch) => {
    const END_POINT = `/policies/applications?filter[quote.reference]=${quoteReference}&filter[version]=all`;
    return Axios.get(END_POINT)
      .then((res) => {
        dispatch({
          type: policyActions.QUOTES_HISTORY,
          data: res.data,
        });
        return res.data;
      })
      .catch((error) => {
        handleErrorResponse(error, 'There was an error loading the resource');
      });
  };
}

export function addToPrintJobs(application, resourceType, queue = '', callback) {
  return (dispatch) => {
    dispatch(isGeneratingDocs(true));
    const queuePath = queue ? `:print-queue-${queue}` : ':print-queue';
    return Axios.post(`${resourceType}/${application.data.id}${queuePath}`, {})
      .then(() => {
        return dispatch(
          getPolicy(application.data.id, resourceType === 'policies/applications'),
        );
      })
      .then(() => {
        toastr.success('Success', 'Successfully added to print queue');
        dispatch(isGeneratingDocs(false));

        if (callback) {
          callback();
        }

        return true;
      })
      .catch((error) => {
        dispatch(isGeneratingDocs(false));
        handleErrorResponse(error, 'Failed to add to print queue');
      });
  };
}

export function removeFromPrintJobs(application, resourceType, queue = '', callback) {
  return (dispatch) => {
    dispatch(isGeneratingDocs(true));
    const queuePath = queue ? `:print-queue-${queue}` : ':print-queue';
    return Axios.delete(`${resourceType}/${application.data.id}${queuePath}`, {})
      .then(() => {
        return dispatch(
          getPolicy(application.data.id, resourceType === 'policies/applications'),
        );
      })
      .then(() => {
        toastr.success('Success', 'Successfully removed from print queue');
        dispatch(isGeneratingDocs(false));

        if (callback) {
          callback();
        }

        return true;
      })
      .catch((error) => {
        dispatch(isGeneratingDocs(false));
        handleErrorResponse(error, 'Failed to remove from print queue');
      });
  };
}

export function sendCustomerLoginEmail(resourceType, id) {
  return () => {
    return Axios.post(`/emails/${resourceType}/${id}:send-customer-log-in`)
      .then(() => {
        toastr.success('Success', 'Customer log in email sent');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Email failed to send');
      });
  };
}

export function sendEndorsementEmail(id) {
  return () => {
    return Axios.post(`/emails/policies/${id}:send-endorsements-email`)
      .then(() => {
        toastr.success('Success', 'Endorsement email sent');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Email failed to send');
      });
  };
}

export function sendCancellationEmail(id) {
  return () => {
    return Axios.post(`/emails/policies/${id}:send-cancellation-email`)
      .then(() => {
        toastr.success('Success', 'Cancellation email sent');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Email failed to send');
      });
  };
}

export function sendCustomerResetPasswordEmail(id, type = 'policies') {
  const endpoint = `/emails/${type}/`;
  return () => {
    return Axios.post(`${endpoint + id}:send-reset-password`)
      .then(() => {
        toastr.success('Success', 'Customer reset password email sent');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Email failed to send');
      });
  };
}

export function emailDocuments(values, application, resourceType) {
  return () => {
    return Axios.post(`${resourceType}/${application.data.id}:email-documents`, values)
      .then(() => {
        toastr.success(
          'Success',
          `Documents have been emailed${
            values.meta.email ? ` to ${values.meta.email}` : ''
          }`,
        );
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Documents failed to send');
      });
  };
}

export function sendSms(values, application, resourceType) {
  return () => {
    return Axios.post(`${resourceType}/${application.data.id}:send-sms`, values)
      .then(() => {
        toastr.success(
          'Success',
          `SMS has been sent${values.meta.number ? ` to ${values.meta.number}` : ''}`,
        );
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to send SMS');
      });
  };
}

export function transferPolicy(values, application, resourceType) {
  return () => {
    return Axios.post(`${resourceType}/${application.data.id}:transfer`, values)
      .then(() => {
        toastr.success('Success', 'Policy transferred to Broker');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to transfer policy to Broker');
      });
  };
}

export function cancelPolicy(values, policy) {
  return (dispatch) => {
    return Axios.post(`policies/${policy.data.id}:cancel`, values)
      .then(() => {
        dispatch(getPolicy(policy.data.id));
        toastr.success('Success', 'Policy has been cancelled');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to cancel policy');
      });
  };
}

export function logLeaveQuoteReason(values, mta) {
  const eventurl = mta
    ? '/events/log-leave-mta-reason'
    : '/events/log-leave-quote-reason';

  return () => {
    return Axios.post(eventurl, values)
      .then(() => {
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to cancel policy');
      });
  };
}

export function uncancelPolicy(policy) {
  return (dispatch) => {
    return Axios.post(`policies/${policy.data.id}:uncancel`)
      .then(() => {
        dispatch(getPolicy(policy.data.id));
        toastr.success('Success', 'Policy has been uncancelled');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to uncancel policy');
      });
  };
}

export function lapseQuote(values, application) {
  return (dispatch) => {
    return Axios.post(`policies/applications/${application.data.id}:lapse`, values)
      .then(() => {
        dispatch(getPolicy(application.data.id, true));
        toastr.success('Success', 'Quote has been lapsed');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to lapse quote');
      });
  };
}

export function logEditQuote(application) {
  return () => {
    return Axios.post(`policies/applications/${application.data.id}:log-edit`).then(
      () => {
        return true;
      },
    );
  };
}

export function getDetailsForCancellation(applicationId, application, cb) {
  return (dispatch) => {
    dispatch(isCheckingCancel(true));
    return Axios.post(`policies/${applicationId}/premiums/return-premium`, application)
      .then((response) => {
        cb(response.data);
        dispatch(isCheckingCancel(false));
        return true;
      })
      .catch((error) => {
        dispatch(isCheckingCancel(false));
        handleErrorResponse(error, 'Policy cannot be canceled at this time');
      });
  };
}

export function updateEndorsement(values) {
  return () => {
    return Axios.patch(`policies/endorsements/${values.data.id}`, values)
      .then(() => {
        toastr.success('Success', 'Endorsement details updated');
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to update endorsement details');
      });
  };
}

export function updateRenewalStatus(application, formValues, status) {
  return (dispatch) => {
    const values = { data: { attributes: { renewal_status: status } } };
    values.meta = { reason: '' };

    if (formValues.meta) {
      if (formValues.meta.reason) {
        values.meta.reason = formValues.meta.reason;
      }

      if (formValues.meta.note && formValues.meta.note.content) {
        values.meta.reason = formValues.meta.note.content;
      }
    }

    return Axios.post(`policies/${application.data.id}:update-renewal-status`, values)
      .then(() => {
        toastr.success('Success', `Renewal status changed to ${status}`);
        dispatch(getPolicy(application.data.id));
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Error');
      });
  };
}

// Receives an array of ID
export function inviteRenewalsArray(applications, convertToManual) {
  return (dispatch) => {
    const data = { meta: { applications } };
    if (convertToManual) {
      data.meta.metadata = { convert_referred_renewal_to_manual: true };
    }
    return Axios.post('policies/applications:invite-renewals', data)
      .then(() => {
        toastr.success('Success', 'Renewal has been invited');
        dispatch(clearData());
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to invite renewal');
      });
  };
}

export function addNonRenewals(values) {
  return () => {
    return Axios.post(`policies/${null}:update-from-file`, values)
      .then(() => {
        toastr.success(
          'Success',
          'You will receive an email when the upload is complete',
        );
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to add to non renewal list');
      });
  };
}

export function lapseQuotesArray(values) {
  return (dispatch) => {
    return Axios.post('policies/applications:lapse', values)
      .then(() => {
        toastr.success('Success', 'Lapsed');
        dispatch(clearData());
        return true;
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to lapse');
      });
  };
}

const updateSource = (policyId, values) => {
  return () => {
    return Axios.patch(`policies/${policyId}/update-source`, values)
      .then(() => {
        toastr.success('Success', 'Source Updated');
      })
      .catch((error) => {
        handleErrorResponse(error, 'Failed to update source');
      });
  };
};

export const actions = {
  updateSource,
  getPolicy,
};
